<script setup lang="ts">
import { ElDropdown, ElDropdownItem, ElDropdownMenu } from 'element-plus'
import { computed } from 'vue'

import { BoxComponent, ButtonComponent, TypographyComponent } from '../../'
import AddIcon from '../../../assets/icons/add-plus.svg?component'
import { FILTER_CRITERION_CATEGORY_OPTIONS } from '../../../constants'
import { FilterCriterionCategoryEnum } from '../../../types'

const props = withDefaults(defineProps<{ categories?: FilterCriterionCategoryEnum[] }>(), {
  categories: () => Object.values(FilterCriterionCategoryEnum),
})
const emits = defineEmits<(e: 'select', category: FilterCriterionCategoryEnum) => void>()

const options = computed(() => FILTER_CRITERION_CATEGORY_OPTIONS.filter((option) => props.categories.includes(option.value)))

const handleCommand = (category: FilterCriterionCategoryEnum) => emits('select', category)
</script>

<template>
  <ElDropdown trigger="click" @command="handleCommand">
    <ButtonComponent variant="text" class="add-criteria-button">
      <template #iconLeft>
        <AddIcon />
      </template>
      <TypographyComponent variant="label-large" :font-weight="600">Add criteria</TypographyComponent>
    </ButtonComponent>
    <template #dropdown>
      <BoxComponent :w="240" class="add-criteria-button__menu">
        <ElDropdownMenu>
          <ElDropdownItem v-for="option in options" :key="option.id" :command="option.value">
            <BoxComponent :p="16" w="100%" class="add-criteria-button__item">
              <TypographyComponent variant="body-large">{{ option.label }}</TypographyComponent>
            </BoxComponent>
          </ElDropdownItem>
        </ElDropdownMenu>
      </BoxComponent>
    </template>
  </ElDropdown>
</template>

<style scoped lang="scss">
.button.add-criteria-button {
  padding: 8px 16px 8px 12px;
  gap: 8px;

  &:hover {
    background: var(--black-8);
  }
}

/*
 * The popper already draws the menu surface, shadow included. A second shadow on the menu gets
 * clipped by Element Plus's rectangular scrollbar and leaks out as square corners, so the menu only
 * rounds its content off to the popper's corners.
 */
.add-criteria-button__menu {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.el-dropdown-menu) {
  padding: 0;
}

:deep(.el-dropdown-menu__item) {
  padding: 0;
  white-space: normal;
}
</style>

<style lang="scss">
.el-dropdown-menu__item:not(.is-disabled):hover,
.el-dropdown-menu__item:not(.is-disabled):focus {
  background: rgba(34, 34, 34, 0.04);
}
</style>
